/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: CreateProjectDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * CreateProjectDialog - Диалог создания проекта с превью карточки портфеля.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: заголовок портфеля, live-превью карточки, anti-autocomplete, primary CTA.
 * LAST_CHANGE: 2026-07-13 - Общие хуки blur-валидации и form submit по Enter.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блок отправки.
 * END_CHANGE_SUMMARY
 */

import { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ProjectAvatar from '../../../components/ProjectAvatar';
import { getColorByName } from '../../../utils/projectColors';
import { APP_FORM_AUTOCOMPLETE, mergeAppDataFieldSlotProps } from '../../../utils/formFieldConfig';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isNonEmptyTrimmed } from '../../../utils/formValidators';

const displayFont = '"Syne", "Inter", sans-serif';

/* START_METHOD_CreateProjectDialog */
/* START_CONTRACT: CreateProjectDialog
 * PURPOSE: Диалог создания проекта (название) с валидацией и превью карточки.
 * INPUTS: { open: boolean, onClose: ()=>void, onSubmit: (payload)=>Promise }.
 * OUTPUTS: { JSX.Element - диалог }.
 * SIDE_EFFECTS: вызов onSubmit (HTTP наверх); локальный state.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: CreateProjectDialog
 */
export default function CreateProjectDialog({ open, onClose, onSubmit }) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const [loading, setLoading] = useState(false);

  const trimmedName = nameField.value.trim();
  const previewName = trimmedName || 'Название проекта';
  const accentColor = trimmedName ? getColorByName(trimmedName) : 'text.disabled';

  const canSave = nameField.isValid;

  /* START_METHOD_handleSubmit */
  /* START_CONTRACT: handleSubmit
   * PURPOSE: Проверить форму и создать проект; при успехе закрыть диалог.
   * INPUTS: { event?: React.FormEvent }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onSubmit (HTTP); setLoading; handleClose.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleSubmit
   */
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      nameField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    try {
      await onSubmit({ name: trimmedName });
      /* START_BLOCK_SUBMIT_OK */
      handleClose();
      /* END_BLOCK_SUBMIT_OK */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSubmit */

  /* START_METHOD_handleClose */
  /* START_CONTRACT: handleClose
   * PURPOSE: Сбросить поле названия и закрыть диалог.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setName; onClose.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleClose
   */
  const handleClose = () => {
    if (loading) return;
    nameField.reset('');
    resetFormReady();
    onClose();
  };
  /* END_METHOD_handleClose */

  const helperText = nameField.showError
    ? 'Введите название проекта'
    : 'Как команда или продукт будет отображаться в портфеле';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="create-project-title"
      slotProps={{
        transition: dialogTransitionSlotProps,
        paper: { sx: { overflow: 'hidden' } },
      }}
    >
      <form onSubmit={handleSubmit} noValidate autoComplete={APP_FORM_AUTOCOMPLETE}>
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundImage: (theme) =>
              `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 100%)`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Портфель
          </Typography>
          <Typography
            id="create-project-title"
            variant="h5"
            component="h2"
            sx={{
              fontFamily: displayFont,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            Новый проект
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            После создания добавьте роли, сотрудников и задачи команды.
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 2.5 }}>
          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              pl: 2.5,
              pr: 2,
              py: 1.5,
              mb: 2.5,
              overflow: 'hidden',
              borderColor: trimmedName ? alpha(accentColor, 0.25) : 'divider',
              bgcolor: trimmedName ? alpha(accentColor, 0.04) : 'background.default',
              transition: 'border-color 0.24s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: trimmedName ? accentColor : 'divider',
                transition: 'background-color 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
            <ProjectAvatar name={trimmedName} size={36} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                noWrap
                color={trimmedName ? 'text.primary' : 'text.disabled'}
              >
                {previewName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {trimmedName ? 'Так проект появится в списке' : 'Превью карточки'}
              </Typography>
            </Box>
          </Paper>

          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Название"
            name="teamplanner-project-name"
            placeholder="Например, Мобильное приложение"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            size="small"
            disabled={loading}
            error={nameField.showError}
            helperText={helperText}
            slotProps={mergeAppDataFieldSlotProps({
              htmlInput: { maxLength: 200 },
            })}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Создать проект
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
/* END_METHOD_CreateProjectDialog */
